import { TAcquisitionProviderFacade } from "../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade";
import { useFeatureFlags } from "../../providers/FeatureFlagProvider/FeatureFlagProvider";
import { usePushdownContext } from "../../providers/Pushdown/PushdownProvider";
import { hasDefaultComponent } from "../../utils/pageModelUtils";
import sortPushdownErrors from "./sortPushdownErrors";
import { IFieldName } from "../../providers/Pushdown/util/getPushdownError";
import { plasticCardDesignFieldValidations } from "./FieldValidations/CtaFieldValidations-PlasticCardValidation";
import { phoneFieldValidations } from "./FieldValidations/CtaFieldValidations-PhoneValidation";
import { incomeFieldValidations } from "./FieldValidations/CtaFieldValidations-IncomeValidation";
import { personalInformationFieldValidations, ssnFieldValidation } from "./FieldValidations/CtaFieldValidations-PersonalInformationValidation";
import { applicationEsignFieldValidations, ConsumerAcknowledgementFieldValidations, termsAndConditionsFieldValidations } from "./FieldValidations/CtaFieldValidations-RequiredAgreements";
import { authorizedUserFieldValidations, creditProtectionFieldValidations } from "./FieldValidations/CtaFieldValidations-AncillaryProducts";
import { TFieldState } from "../../providers/ModernAcquisitionProvider/types";
import { TFieldValidationFunction, TFieldValidationResult, ValidationAbortError } from "./FieldValidations/CtaFieldValidations.types";

const convertErrorMessage = (errorMsg: string) => {
    switch (errorMsg) {
        case 'invalidInput': return 'NotValid';
        case 'thirdParty': return 'ThirdParty';
        case 'notEligible': return 'NotEligible';
        case 'requiredField':
        case 'None':
        default: return 'RequiredField'; 
    }
};

const useHandleErrorPushdown = (): {
    handleErrorPushdown: (validatingState: TAcquisitionProviderFacade) => Promise<void>
} => {

    const { showPushdown } = usePushdownContext();
    const { getFeatureFlags } = useFeatureFlags();

    const handleErrorPushdown = async (
        validatingState: TAcquisitionProviderFacade,
    ): Promise<void> => {
        let fieldErrors: TFieldValidationResult = {};
        const acquisitionData = validatingState.getPersonalInformation();

        const ComponentNameMap: Record<
            string, 
            TFieldValidationFunction[]
        > = {
            'application-esign': [ applicationEsignFieldValidations ],
            'authorized-user': [ authorizedUserFieldValidations ],
            'credit-protection': [ creditProtectionFieldValidations ],
            'consumer-report': [ ConsumerAcknowledgementFieldValidations ],
            'personal-information-form': [ 
                personalInformationFieldValidations,
                phoneFieldValidations,
                incomeFieldValidations,
                ssnFieldValidation,
            ],
            'phone-number-and-income': [
                phoneFieldValidations,
                incomeFieldValidations,
            ],
            'plastic-card-design': [ plasticCardDesignFieldValidations ],
            'ssn-module': [ ssnFieldValidation ],
            'terms-and-conditions': [ termsAndConditionsFieldValidations ],
        };
        try {
            for (const componentName in ComponentNameMap) {
                if (hasDefaultComponent(`cob-corp-acquisition/components/creditonebank/${componentName}`)) {
                    for (const validationFunction of ComponentNameMap[componentName]) {
                        const validationResult = validationFunction(validatingState);
                        if (validationResult instanceof Promise) {
                            fieldErrors = {
                                ...fieldErrors,
                                ...(await validationResult),
                            };
                        } else {
                            fieldErrors = {
                                ...fieldErrors,
                                ...validationResult,
                            }
                        }
                    }
                }
            }
        } catch (e: unknown) {
            if (e instanceof ValidationAbortError) {
                if (e.pushdownToShow) {
                    showPushdown(e.pushdownToShow);
                    console.error(e.message);
                }
                return;
            }
        }

// NEW PROVIDER REPLACEMENT
        // If there is an existing error for the key, remove it, and assert the Field Error.
        // Refer to modernReadManager.tsx for similar implementation
        const validationMigrationMap: Partial<Record<IFieldName, keyof typeof acquisitionData>> = {};

        if (getFeatureFlags('DPM-13102')) {
            validationMigrationMap.FirstName = 'firstName';
            validationMigrationMap.LastName = 'lastName';
            validationMigrationMap.NameSuffix = 'suffix';
            // --- Always Disabled / Optional ---
            // validationMigrationMap.MiddleInitial = 'middleInitial';
        }

        if (getFeatureFlags('DPM-13566')) {
            validationMigrationMap.EmailAddress = 'emailAddress';
        }

        const overriddenFields = [
            ...Object
                .entries(fieldErrors)
                .filter(([fieldName]) => !(fieldName in validationMigrationMap)),
            ...Object
                .entries(fieldErrors)
                .filter(([fieldName]) => (fieldName in validationMigrationMap))
                .filter(([fieldName]) => (acquisitionData[validationMigrationMap[fieldName as keyof typeof acquisitionData]]?.state === "error"))
                .map(([fieldName]) => ([fieldName, convertErrorMessage(acquisitionData[validationMigrationMap[fieldName as keyof typeof acquisitionData]].errorType)]))
        ] as unknown as [IFieldName, TFieldState];

        if (overriddenFields.length > 0) {
            return showPushdown({
                header: 'Problem Processing Your Request.',
                description:
                    'The following fields and/or sections require your attention:',
                message: [],
                fieldErrors: sortPushdownErrors(overriddenFields as unknown as Array<[string, string]>),
                monthlyIncome: acquisitionData?.monthlyIncome?.value || '',
                color: 'error',

                // DPM-13680 for swapping HomeAddress and MailingAddress in the pushdown errors
            }, validatingState.getIsHomeAddressShown());
        }
    };

    return {
        handleErrorPushdown,
    };
};

export default useHandleErrorPushdown;